/**
 * Sistem Ayarları — System Settings (backlog #243, FDS-Backend V54).
 *
 * Java: GET  /api/v1/system-settings?group=kroki   → [{ key, group, type, label, description, value,
 *                                                      defaultValue, isDefault, min, max,
 *                                                      updatedAt, updatedBy, updatedByName }]
 *       GET  /api/v1/system-settings/{key}
 *       PUT  /api/v1/system-settings/{key}  { value }   (SYSTEM_SETTINGS_MANAGE — Admin/Süper Admin)
 *       GET  /api/v1/system-settings/{key}/history      (aynı yetki) → en yeni 50 değişiklik
 *
 * Anahtarların listesi, tipleri ve varsayılanları backend'de (SystemSettingConstant); bir anahtara
 * hiç yazılmadıysa değeri varsayılandır. Varsayılana dönmek = varsayılan değeri PUT etmek.
 */

import axios from 'axios';
import { JAVA_BACKEND_API_URL, getAuthHeader } from '../Components/Contexts/BackendConstants';
import i18n from "../i18n";

const BASE = `${JAVA_BACKEND_API_URL}/system-settings`;

/** Kroki / plan ayarları grubu. */
export const SETTING_GROUP_KROKI = 'kroki';

/** Stand Detay › Konum görünümlerinin kenar payları (backlog #242), metre, MARGINS tipi. */
export const KONUM_MARGIN_KEYS = {
  alan:  'kroki.konum.alan.margins',
  salon: 'kroki.konum.salon.margins',
  stand: 'kroki.konum.stand.margins',
};

/**
 * Ayarlar okunamazsa (backend henüz V54 ile yeniden başlatılmadı, ağ hatası …) kullanılan yedek —
 * backend'in SystemSettingConstant varsayılanlarıyla AYNI olmalı.
 */
export const KONUM_MARGIN_FALLBACK = {
  alan:  { left: 10, top: 10, right: 10, bottom: 10 },
  salon: { left: 2, top: 2, right: 2, bottom: 2 },
  stand: { left: 1, top: 1, right: 1, bottom: 1 },
};

function toError(error, fallback) {
  const status = error?.response?.status ?? null;
  const data = error?.response?.data;
  let message = fallback;
  if (status === 401) message = i18n.t("services:systemSettingsService.oturumGecersiz401LutfenYeniden");
  else if (status === 403) message = i18n.t("services:systemSettingsService.buIslemIcinYetkinizYok");
  else if (data && typeof data === 'object' && data.message) message = data.message;
  else if (typeof data === 'string' && data.trim()) message = data;
  else if (error?.message) message = error.message;
  const err = new Error(message);
  err.status = status;
  return err;
}

/** Bir grubun (ya da hepsinin) ayarları. */
export async function listSystemSettings(group) {
  try {
    const res = await axios.get(BASE, { headers: getAuthHeader(), params: group ? { group } : undefined });
    return Array.isArray(res.data) ? res.data : [];
  } catch (e) { throw toError(e, i18n.t("services:systemSettingsService.sistemAyarlariYuklenemedi")); }
}

/** Bir ayarı kaydeder; dönüş güncel ayar satırı. 400: değer tanıma uymuyor (mesaj Türkçe). */
export async function updateSystemSetting(key, value) {
  try {
    const res = await axios.put(`${BASE}/${encodeURIComponent(key)}`, { value }, { headers: getAuthHeader() });
    return res.data;
  } catch (e) { throw toError(e, 'Ayar kaydedilemedi.'); }
}

/** #199 Kayıt Ayarları grubu (FDS-Backend #198): dört BOOLEAN anahtar, kural × kitle (Yönetim / Firma). */
export const SETTING_GROUP_KAYIT = 'kayit';

/** #199: backend SystemSettingConstant.KAYIT_* ile aynı. */
export const KAYIT_KEYS = {
  VERGI_NO_YONETIM:    'kayit.vergi_no.yonetim',    // varsayılan false (#65: personel muaf)
  VERGI_NO_FIRMA:      'kayit.vergi_no.firma',      // varsayılan true (#40)
  FIRMA_BELGE_YONETIM: 'kayit.firma_belge.yonetim', // varsayılan true
  FIRMA_BELGE_FIRMA:   'kayit.firma_belge.firma',   // varsayılan true
  KISI_UYRUK_ESASI:    'kayit.kisi_uyruk_esasi',    // varsayılan false (#430): kişisel belgeler KİŞİNİN uyruğuna göre
  PORTAL_EGITIM:       'kayit.portal_egitim',       // varsayılan false: firma portalının kullanım turu (Katılımcı girişleri)
  // #704 (backend #703, SystemSettingConstant.PORTAL_SADE_PANEL): varsayılan false. Kayıt grubunda ama anahtarı `portal.` ile başlar.
  PORTAL_SADE_PANEL:   'portal.sade_panel',         // her firma girişi Yabancı Katılımcı'nın sade panelini görür (felaket anahtarı)
};

/**
 * Firma portalı kullanım turu açık mı (`kayit.portal_egitim`, FDS-Backend SystemSettingConstant.KAYIT_PORTAL_EGITIM). Her giriş
 * okuyabilir (GET yalnızca oturum ister); yazmak SYSTEM_SETTINGS_MANAGE ister. Okunamazsa ya da backend anahtarı henüz bilmiyorsa
 * KAPALI: bir ayar okunamadı diye tur çıkmaz.
 */
export async function getPortalEgitimAcik() {
  try {
    const list = await listSystemSettings(SETTING_GROUP_KAYIT);
    return list.some((s) => s.key === KAYIT_KEYS.PORTAL_EGITIM && s.value === true);
  } catch {
    return false;
  }
}

/** #162 Dosya Ayarları grubu: anahtarlar `dosya.<kategori>.max_mb` (NUMBER) ve `dosya.<kategori>.uzantilar` (EXTENSIONS). */
export const SETTING_GROUP_DOSYA = 'dosya';

/**
 * #162: sunucunun kendi yükleme tavanları (ayar değil; uygulama açılırken okunur) ve her kategorinin etkin
 * sınırı: { sunucuDosyaSiniriMb, sunucuIstekSiniriMb, kategoriler: [{ kod, ad, aciklama, maxMbKey, uzantiKey,
 * etkinMaxMb, uzantilar }] }
 */
export async function getDosyaOzet() {
  try {
    const res = await axios.get(`${BASE}/dosya-ozet`, { headers: getAuthHeader() });
    return res.data;
  } catch (e) { throw toError(e, i18n.t("services:systemSettingsService.dosyaAyarlariYuklenemedi")); }
}

/** Bir ayarın en yeni 50 değişikliği. */
export async function getSystemSettingHistory(key) {
  try {
    const res = await axios.get(`${BASE}/${encodeURIComponent(key)}/history`, { headers: getAuthHeader() });
    return Array.isArray(res.data) ? res.data : [];
  } catch (e) { throw toError(e, i18n.t("services:systemSettingsService.ayarGecmisiYuklenemedi")); }
}

const isMargins = (v) => v && ['left', 'top', 'right', 'bottom'].every((k) => Number.isFinite(Number(v[k])));

/**
 * Stand Detay › Konum'un üç görünümünün kenar payları: { alan, salon, stand, fromSettings }.
 * Okunamayan her anahtar için KONUM_MARGIN_FALLBACK kullanılır; fromSettings=false ise hiçbiri
 * sunucudan gelmedi.
 */
export async function getKonumMargins() {
  try {
    const rows = await listSystemSettings(SETTING_GROUP_KROKI);
    const byKey = Object.fromEntries(rows.map((r) => [r.key, r.value]));
    const out = { fromSettings: true };
    Object.entries(KONUM_MARGIN_KEYS).forEach(([view, key]) => {
      out[view] = isMargins(byKey[key]) ? byKey[key] : KONUM_MARGIN_FALLBACK[view];
    });
    return out;
  } catch {
    return { ...KONUM_MARGIN_FALLBACK, fromSettings: false };
  }
}
